import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader, { PublicFooter } from "@/components/PublicHeader";

export default function PublicLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);

  // Signed-in learners skip the marketing page and go to their product/picker (§2).
  useEffect(() => {
    if (user && typeof user === "object") {
      if (Array.isArray(user.products) && user.products.length === 1) navigate(`/${user.products[0]}`, { replace: true });
      else navigate("/products", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => { api.get("/public/catalog").then((res) => setProducts(res.data.products)); }, []);

  if (user === null || (user && typeof user === "object")) return <div className="loading">Loading…</div>;
  if (!products) return <div className="loading">Loading the Academy…</div>;

  return (
    <div className="app-root">
      <PublicHeader />
      <div className="app-main">
        <section className="hero on-dark" data-testid="landing-hero">
          <div className="hero__inner">
            <div className="eyebrow">Included with every EQUIPR subscription</div>
            <h1 className="hero__title">Learn your role in the product you actually use.</h1>
            <p className="hero__sub">
              The EQUIPR Academy teaches your team their role in each product, start to finish. Short video modules follow the real workflows, a knowledge check closes each section, and a final assessment certifies you.
            </p>
            <button className="btn btn--yellow" style={{ marginBottom: "44px" }} onClick={() => navigate("/login")} data-testid="landing-signin">Sign in to start</button>

            <div className="eyebrow" style={{ marginBottom: "16px" }}>The courses — one per product</div>
            <div className="grid-products" data-testid="landing-product-grid">
              {products.map((p) => (
                <button
                  key={p.id}
                  className={`pcard on-dark ${p.live ? "pcard--live" : "pcard--locked"}`}
                  onClick={() => p.live && navigate(`/preview/${p.id}`)}
                  disabled={!p.live}
                  aria-disabled={!p.live}
                  data-testid={`landing-product-${p.id}`}
                >
                  <div className="pcard__top">
                    <span className={`tag ${p.live ? "tag--live" : "tag--muted"}`}>{p.tag}</span>
                    <span className={`status ${p.live ? "status--live" : "status--muted"}`}>
                      {p.status === "live" ? "Live" : p.status === "production" ? "In production" : "Planned"}
                    </span>
                  </div>
                  <div className="pcard__name">{p.name}</div>
                  <p className="pcard__blurb">{p.blurb}</p>
                  <div className="pcard__chips">
                    {p.chips.map((c, i) => <span key={i} className="chip chip--dark">{c}</span>)}
                  </div>
                  <div className={`pcard__cta ${p.live ? "pcard__cta--live" : "pcard__cta--locked"}`}>
                    {p.live ? "View the course →" : "Notify me"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="page" data-testid="landing-info">
          <div className="container grid-cream">
            <div className="icard">
              <div className="eyebrow">Certified</div>
              <div className="icard__title">A certificate your account owner can see</div>
              <p className="icard__body">Every path ends in a final assessment. Pass it and the Academy issues a certificate that names the product and the role — verifiable by a short link.</p>
            </div>
            <div className="icard">
              <div className="eyebrow">In-person</div>
              <div className="icard__title">Get your whole team running</div>
              <p className="icard__body">Paid onsite sessions per role, built on your own jobs, price lists and integration. Quoted per product engagement, with the Academy path as pre-work.</p>
            </div>
            <div className="icard">
              <div className="eyebrow">Help Center</div>
              <div className="icard__title">Just need one answer?</div>
              <p className="icard__body">The Academy teaches a role start to finish. The Help Center answers a single task and stays where it is, per product.</p>
            </div>
          </div>
        </section>
      </div>
      <PublicFooter />
    </div>
  );
}
